import React, { useEffect, useRef, useState } from 'react';
import { Html5Qrcode, Html5QrcodeSupportedFormats, Html5QrcodeScannerState } from 'html5-qrcode';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { ScanLine, X } from 'lucide-react';
import { toast } from 'sonner';

// Supports both QR and common 1D barcodes (EAN, UPC, Code 128, Code 39).
const BARCODE_FORMATS = [
  Html5QrcodeSupportedFormats.QR_CODE,
  Html5QrcodeSupportedFormats.EAN_13,
  Html5QrcodeSupportedFormats.EAN_8,
  Html5QrcodeSupportedFormats.UPC_A,
  Html5QrcodeSupportedFormats.UPC_E,
  Html5QrcodeSupportedFormats.CODE_128,
  Html5QrcodeSupportedFormats.CODE_39,
  Html5QrcodeSupportedFormats.CODE_93,
  Html5QrcodeSupportedFormats.ITF,
];

// Safely stop and clear a scanner instance. Handles every edge-case the library
// throws (sync throw when NOT_STARTED, async reject on stop, teardown mid-start).
const safeStop = async (scanner) => {
  if (!scanner) return;
  try {
    const state = scanner.getState && scanner.getState();
    if (state === Html5QrcodeScannerState.SCANNING || state === Html5QrcodeScannerState.PAUSED) {
      await scanner.stop();
    }
  } catch {
    /* noop */
  }
  try { scanner.clear(); } catch { /* noop */ }
};

export const QrScanner = ({ open, onClose, onScan, title = 'Scan QR Code', hint = 'Point your camera at a Lutrom asset or report QR code.' }) => {
  const scannerRef = useRef(null);
  const elementId = 'qr-reader-region';

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    let localScanner = null;

    const start = async () => {
      try {
        localScanner = new Html5Qrcode(elementId, { formatsToSupport: BARCODE_FORMATS });
        await localScanner.start(
          { facingMode: 'environment' },
          { fps: 10, qrbox: { width: 260, height: 160 } },
          (decoded) => {
            onScan(decoded);
            safeStop(localScanner);
          },
          () => { /* ignore parse errors */ }
        );
        if (cancelled) {
          // Unmounted while starting — stop immediately.
          safeStop(localScanner);
          return;
        }
        scannerRef.current = localScanner;
      } catch (e) {
        if (!cancelled) {
          toast.error('Unable to access camera. Please grant permission.');
          onClose();
        }
        // Don't leave an un-started scanner hanging.
        safeStop(localScanner);
      }
    };

    const t = setTimeout(start, 200);
    return () => {
      cancelled = true;
      clearTimeout(t);
      const s = scannerRef.current || localScanner;
      scannerRef.current = null;
      safeStop(s);
    };
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="qr-scanner-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><ScanLine className="h-5 w-5" /> {title}</DialogTitle>
          <DialogDescription>{hint}</DialogDescription>
        </DialogHeader>
        <div id={elementId} className="w-full rounded-md overflow-hidden bg-black" style={{ minHeight: 300 }} />
        <Button variant="outline" onClick={onClose} data-testid="qr-scanner-cancel-btn"><X className="h-4 w-4 mr-2" /> Cancel</Button>
      </DialogContent>
    </Dialog>
  );
};

export const QrManualInput = ({ onSubmit }) => {
  const [value, setValue] = useState('');
  return (
    <form onSubmit={(e) => { e.preventDefault(); if (value) onSubmit(value); }} className="flex gap-2">
      <input className="flex-1 border border-slate-200 rounded px-3 h-9 text-sm" placeholder="Or paste QR URL..." value={value} onChange={e => setValue(e.target.value)} />
      <Button type="submit" size="sm">Open</Button>
    </form>
  );
};
